import {
    AfterViewInit, Component, EventEmitter, Input, Output, ViewChild
} from '@angular/core';
import { IdentificationStepsService } from '../../services/identification-steps.service';
import { UnauthenticatedUserRepositoryService } from '../../services/unauthenticated-user-repository.service';
import { ClientModel } from '../../../shared/models/client.model';
import { FormGroup, NG_VALIDATORS, NgModel } from '@angular/forms';
import { ValidDocument1ValidatorDirective } from '../../../shared/validators/valid-document1.directive';
import { ExistsDocument1ValidatorDirective } from '../../../shared/validators/exists-document1.directive';
import { MatchValidatorDirective } from '../../../shared/validators/match.directive';
import { FacebookUserModel } from '../../../shared/models/facebook.user.model';
import { GoogleUserModel } from '../../../shared/models/google.user.model';
import { IdentificationNewUserError } from '../../../shared/models/errors/identification-new-user.error';
import { ClientTelephone } from '../../../shared/models/client-telephone';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { ValidTelephoneValidatorDirective } from '../../../shared/validators/valid-telephone.directive';

@Component({
    selector: 'payment-identification-new-user',
    templateUrl: './payment.identification.new-user.component.html',
    styleUrls: ['../../payment.component.scss'],
    providers: [{
        provide: NG_VALIDATORS,
        useExisting: ValidDocument1ValidatorDirective,
        multi: true
    },{
        provide: NG_VALIDATORS,
        useExisting: ValidTelephoneValidatorDirective,
        multi: true
    }, {
        provide: NG_VALIDATORS,
        useExisting: ExistsDocument1ValidatorDirective,
        multi: true
    }, {
        provide: NG_VALIDATORS,
        useExisting: MatchValidatorDirective,
        multi: true
    }]
})
export class PaymentIdentificationNewUserComponent implements
    AfterViewInit {
    @ViewChild('f') formNewUser: FormGroup;
    protected socialUser: FacebookUserModel|GoogleUserModel;
    public existentUser: ClientModel = null;
    public isSocial = false;
    public attempts = {
        document1: 0,
        telephone: 0
    };
    public client: ClientModel = new ClientModel;
    public telephoneModel = '';
    public document1Model = '';
    public passConfirm = '';
    public document2Model = '';
    public errors: IdentificationNewUserError;
    public formSubmitted: EventEmitter<any> = new EventEmitter<any>();
    public extraFieldsState = true;
    private extraFieldsData: Object;
    @Output() creatingClient: EventEmitter<ClientModel> = new EventEmitter();
    @Output() createdClient: EventEmitter<ClientModel> = new EventEmitter();
    @Output() failedCreateClient: EventEmitter<ClientModel> = new EventEmitter();
    @Output() resetedEmail: EventEmitter<null> = new EventEmitter();
    @Input() notifyExtraFieldsIsValid: EventEmitter<boolean>;

    constructor(
        protected identificationStepsService: IdentificationStepsService,
        protected unauthenticatedUserRepository: UnauthenticatedUserRepositoryService,
        protected toastr: ToastrService,
        protected translate: TranslateService
    ) {
        this.resetErrors();
        this.client.telephone = new ClientTelephone();
        this.client.email = this.unauthenticatedUserRepository.getEmail();
        this.client.legal_personality = 0;
    }

    ngAfterViewInit() {
        this.isSocial = this.unauthenticatedUserRepository.isSocial();
        if (this.unauthenticatedUserRepository.isSocial()) {
            this.client.name1 = this.unauthenticatedUserRepository.getName1();
            this.client.name2 = this.unauthenticatedUserRepository.getName2();
            this.client.social_network = this.unauthenticatedUserRepository.getSocialNetwork();
            this.client.social_uid = this.unauthenticatedUserRepository.getSocialUid();
            this.socialUser = this.unauthenticatedUserRepository.getSocialUser();
        }
    }

    /**
     * Reset inputted email.
     **/
    public resetEmail() {
        this.unauthenticatedUserRepository.reset();
        this.resetedEmail.emit(null);
    }
    /**
     * legalPersonality property setter.
     **/
    public setLegalPersonality(
        legalPersonality: number,
        document1Model: NgModel,
        document2Model: NgModel
    ) {
        this.client.legal_personality = legalPersonality;
        this.client.document1 = ' ';
        this.client.document2 = ' ';
        document1Model.reset(' ');
        document2Model.reset(' ');
    }
    /**
     * Returns true if legal personality is 0.
     **/
    public registeringPf() {
        return this.client.legal_personality === 0;
    }
    /**
     * Returns true if legal personality is 1.
     **/
    public registeringPj() {
        return this.client.legal_personality === 1;
    }
    /**
     * Create client process. Validate data, emit events and call API.
     **/
    public next(
        name1: NgModel,
        name2: NgModel,
        telephone: NgModel,
        document1: NgModel,
        document2: NgModel
    ) {
        this.formSubmitted.emit(null);
        this.markFormAsDirty();

        const isValid = this.extraFieldsState
            && this.validateFields(name1, name2, telephone, document1, document2);

        if (isValid) {

            this.sanitizeTelephone(telephone.value);
            this.sanitizeDocument1(document1.value);
            this.sanitizeDocument2(document2.value.toString());
            this.prepareReferral();

            this.client.extra_fields = this.extraFieldsData;

            this.resetErrors();
            this.creatingClient.emit(this.client);
            this.identificationStepsService
                .create(this.client, this.socialUser)
                .subscribe(() => {
                    this.createdClient.emit(this.client);
                }, (err) => {
                    this.errors = err.errors || {};
                    this.failedCreateClient.emit(err);
                    this.translate
                        .get('PAYMENT.IDENTIFICATION.NEW_USER.ERRORS.GENERAL')
                        .subscribe(message => this.toastr.error(message));
                });
        }
    }
    /**
     * Remove mask digits from document1
     **/
    private sanitizeDocument1(document1: string) {
        this.client.document1 = document1.replace(/\D/g, '');
    }
    /**
     * Remove mask digits from document2
     **/
    private sanitizeDocument2(document2: string) {
        // If document2 was defined, add it to client; if it's undefined or null, remove it
        if (this.registeringPj()) {
            this.client.document2 = document2.replace(/\D/g, '');
        } else if (this.registeringPf()) {
            delete this.client.document2;
        }
    }
    /**
     * Remove mask digits from telephone
     **/
    private sanitizeTelephone(number: string) {
        const tel = new ClientTelephone();

        tel.ddd = number.substr(0, 4).replace('(', '').replace(')', '').trim();
        tel.number = number.substr(4).replace('-', '').trim();
        this.client.telephone = tel;
    }
    /**
     * Set shareable key into client model from local storage if exists.
     **/
    private prepareReferral() {
        const shareableKey = localStorage.getItem('shareable-key');
        if (null !== shareableKey) {
            this.client.shareable_key = shareableKey;
        }
    }
    /**
     * Returns telephone mask regex as array.
     **/
    public loadTelephoneMask(rawTextString = '') {
        const numericChars = rawTextString.replace(/\D/g, '');
        const length = numericChars.length;

        const telMask = [
            '(', /\d/, /\d/, ')', ' ',
            /\d/, /\d/, /\d/, /\d/, '-',
            /\d/, /\d/, /\d/, /\d/,
        ];
        const celMask = [
            '(', /\d/, /\d/, ')', ' ',
            /\d/, /\d/, /\d/, /\d/, /\d/, '-',
            /\d/, /\d/, /\d/, /\d/,
        ];

        if (length <= 10) {
            return telMask;
        }

        return celMask;
    }
    /**
     * Returns document1 mask depending of legal_personality.
     **/
    public loadDocument1Mask() {
        if (this.registeringPf()) {
            return [
                /\d/, /\d/, /\d/,
                '.',
                /\d/, /\d/, /\d/,
                '.',
                /\d/, /\d/, /\d/,
                '-',
                /\d/, /\d/
            ];
        }

        return [
            /\d/, /\d/, '.',
            /\d/, /\d/, /\d/, '.',
            /\d/, /\d/, /\d/, '/',
            /\d/, /\d/, /\d/, /\d/, '-',
            /\d/, /\d/
        ];
    }
    /**
     * Returns document1 type depending of legal_personality.
     **/
    public loadDocument1Type(): string {
        if (this.registeringPf()) {
            return 'cpf';
        }

        return 'cnpj';
    }
    /**
     * Returns document1 placeholder depending of legal_personality.
     **/
    public loadDocument1Placeholder(): string {
        if (this.registeringPf()) {
            return '999.999.999-99';
        }

        return '99.999.999/9999-99';
    }
    /**
     * Returns 11 or if registering pf or pj
     **/
    public loadDocument1Length(): number {
        if (this.registeringPf()) {
            return 14;
        }

        return 18;
    }
    /**
     * Validate form fields.
     **/
    protected validateFields(
        name1: NgModel,
        name2: NgModel,
        telephone: NgModel,
        document1: NgModel,
        document2: NgModel
    ): boolean {
        return  (name1 && name1.valid) &&
                (name2 && name2.valid) &&
                (telephone && telephone.valid) &&
                (document1 && document1.valid) &&
                (this.registeringPf() || (document2 && document2.valid)) &&
                 this.isValidPassword();
    }

    /**
     * Returns true if isSocial new user or password matches and size is
     * greater then 6.
     **/
    private isValidPassword(): boolean {
        return this.isSocial || (
            this.client &&
            this.client.password &&
            this.client.password.length >= 6 &&
            this.client.password === this.passConfirm
        );
    }

    /**
     * Increments number of attempts for a field
     * */
    public incrementAttempts(model: NgModel): void {
        this.attempts[model.name] += 1;
    }
    /**
     * Clear errors
     **/
    public clearErrors(model: NgModel): void {
        if (this.errors[model.name] !== undefined) {
            delete this.errors[model.name];
        }
    }

    /**
     * Reset errors property, setting new PostCheckoutIdentificationErrors
     * instance
     **/
    private resetErrors(): void {
        this.errors = new IdentificationNewUserError;
    }

    /**
     * Set extraFieldsState property when changed
     * */
    public onExtraFieldsChange(isValid: boolean): void {
        this.extraFieldsState = isValid;
    }

    /**
     * Mark all fields of form as dirty
     * */
    private markFormAsDirty(): void {
        for (const control in this.formNewUser.controls) {
            this.formNewUser.controls[control].markAsDirty();

            if (this.attempts.hasOwnProperty(control)) {
                this.attempts[control] += 1;
            }
        }
    }

    /**
     * Sets extra fields object to be sent for request
     * */
    public setExtraFieldsData(data: Object): void {
        this.extraFieldsData = data;
    }

    /**
     * Check if email is defined in client data
     **/
    public definedEmail() {
        return this.client.email != null;
    }
}



// WEBPACK FOOTER //
// /codebuild/output/src560599644/src/src/app/payment/identification/new-user/payment.identification.new-user.component.ts